import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api, eventTypeLabel, daysUntil } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Plus, Mail, Phone, ArrowUpRight, CalendarDays, MapPin, Pencil, Trash2 } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
      setForm({
        name: data.name || "",
        company: data.company || "",
        primary_contact_name: data.primary_contact_name || "",
        primary_contact_email: data.primary_contact_email || "",
        primary_contact_phone: data.primary_contact_phone || "",
        notes: data.notes || "",
      });
    } catch (e) {
      toast.error("Could not load client");
      navigate("/clients");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const save = async () => {
    try {
      await api.patch(`/clients/${id}`, form);
      toast.success("Client saved");
      setEditOpen(false);
      load();
    } catch (e) { toast.error("Could not save"); }
  };

  const del = async () => {
    if (!confirm("Delete this client? Their events will be un-linked (not deleted).")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client removed");
      navigate("/clients");
    } catch (e) { toast.error("Could not delete"); }
  };

  if (loading || !client) return <Shell><div className="p-10 text-muted">Loading client…</div></Shell>;

  const events = client.events || [];
  const upcoming = events.filter(e => daysUntil(e.event_date) >= 0);
  const past = events.filter(e => daysUntil(e.event_date) < 0);

  return (
    <Shell>
      <div className="p-6 md:p-10 max-w-6xl">
        <Link to="/clients" className="inline-flex items-center gap-2 text-sm text-muted hover:text-app mb-6" data-testid="back-to-clients">
          <ArrowLeft size={14} strokeWidth={1.5} /> All clients
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-6 mb-10">
          <div>
            {client.company && <div className="eyebrow mb-2">{client.company}</div>}
            <h1 className="font-serif text-4xl md:text-5xl text-app tracking-tight leading-none" data-testid="client-detail-name">{client.name}</h1>
            <div className="mt-4 flex items-center gap-6 text-sm text-muted font-light flex-wrap">
              {client.primary_contact_email && (
                <a href={`mailto:${client.primary_contact_email}`} className="flex items-center gap-2 hover:text-app">
                  <Mail size={14} strokeWidth={1.5} />{client.primary_contact_email}
                </a>
              )}
              {client.primary_contact_phone && (
                <span className="flex items-center gap-2"><Phone size={14} strokeWidth={1.5} />{client.primary_contact_phone}</span>
              )}
              {client.primary_contact_name && (
                <span className="text-app">Contact: {client.primary_contact_name}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-client-btn" className="rounded-sm border-[#0A0A0A] text-app hover:bg-surface-2 h-10">
              <Pencil size={14} strokeWidth={1.5} className="mr-2" /> Edit
            </Button>
            <Button variant="ghost" onClick={del} data-testid="delete-client-btn" className="rounded-sm text-[#EF4444] hover:bg-[#EF4444]/10 h-10">
              <Trash2 size={14} strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {client.notes && (
          <div className="bevel border-hair rounded-sm p-5 mb-10">
            <div className="eyebrow mb-2">Notes</div>
            <p className="text-app font-light">{client.notes}</p>
          </div>
        )}

        <section>
          <div className="flex items-baseline justify-between border-b border-hair pb-3 mb-6">
            <div className="flex items-baseline gap-4">
              <h2 className="font-serif text-2xl text-app">Events</h2>
              <span className="eyebrow">{events.length}</span>
            </div>
            <Link to="/dashboard" className="text-xs text-app underline hover:text-[#000000]">
              + Create from dashboard (pick this client)
            </Link>
          </div>

          {events.length === 0 ? (
            <div className="bevel border-hair rounded-sm p-10 text-center">
              <p className="text-muted font-light">No events under this client yet. Create an event from the dashboard and pick <span className="text-app">{client.name}</span> as the client.</p>
            </div>
          ) : (
            <>
              {upcoming.length > 0 && (
                <>
                  <div className="eyebrow mb-3">Upcoming</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {upcoming.map(e => <MiniEventCard key={e.id} ev={e} />)}
                  </div>
                </>
              )}
              {past.length > 0 && (
                <>
                  <div className="eyebrow mb-3">Archived</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {past.map(e => <MiniEventCard key={e.id} ev={e} muted />)}
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg bevel-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-app">Edit client</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <F label="Client name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Company"><Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} /></F>
            <F label="Primary contact — name"><Input value={form.primary_contact_name || ""} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} /></F>
            <F label="Primary contact — email"><Input type="email" value={form.primary_contact_email || ""} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} /></F>
            <F label="Primary contact — phone"><Input value={form.primary_contact_phone || ""} onChange={(e) => setForm({ ...form, primary_contact_phone: e.target.value })} /></F>
            <F label="Notes"><Textarea rows={2} className="rounded-sm" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="client-save-submit" className="rounded-sm bg-app hover:bg-[#000000]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function MiniEventCard({ ev, muted }) {
  const days = daysUntil(ev.event_date);
  return (
    <Link
      to={`/events/${ev.id}`}
      data-testid={`client-event-card-${ev.id}`}
      className={`block bevel border-hair rounded-sm p-4 card-lift ${muted ? "opacity-70" : ""}`}
    >
      <div className="eyebrow mb-1">{eventTypeLabel(ev.event_type)}</div>
      <div className="font-serif text-lg text-app truncate">{ev.title}</div>
      <div className="mt-3 space-y-1 text-xs text-muted font-light">
        <div className="flex items-center gap-1.5">
          <CalendarDays size={11} strokeWidth={1.5} />
          <span>{new Date(ev.event_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
          {days >= 0 && <span className="text-app">· in {days}d</span>}
        </div>
        {ev.venue && <div className="flex items-center gap-1.5"><MapPin size={11} strokeWidth={1.5} /><span className="truncate">{ev.venue}</span></div>}
      </div>
    </Link>
  );
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}
